import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useOrganizationFilter } from '../contexts/OrganizationFilterContext';
export interface BaseFilterSet {
    skip?: number;
    take?: number;
}

export type ChangeEventUnion =
    | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    | { target: { name: string; value: any; type?: string; checked?: boolean } };

export type HandleChangeExtraFilters = (e: ChangeEventUnion) => void;

function useGlobalFilters(props?: any) {
    const { selectedOrganization, clearSelectedOrganization, setSelectedOrganization } =
        useOrganizationFilter();

    const selectedOrganizationId =
        typeof selectedOrganization === 'object'
            ? selectedOrganization?.id
            : selectedOrganization;

    const defaultFilters = {
        skip: 0,
        take: 10,
        ...(selectedOrganizationId && { idOrganizacion: selectedOrganizationId }),
        ...props
    };

    const [filters, setFilters] = useState<any>(defaultFilters || null);
    const [dateFilters, setDateFilters] = useState<{
        fechaInicio: string | null;
        fechaFin: string | null;
    }>({ fechaInicio: null, fechaFin: null });
    const [extraFilters, setExtraFilters] = useState<any>(null);
    const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

    const clearFilters = () => {
        const cleaned = Object.fromEntries(
            Object.entries(defaultFilters).filter(
                ([key]) => !key.includes('fecha') && key !== 'idOrganizacion'
            )
        );

        setFilters(cleaned);
        setExtraFilters(null);
        setDateFilters({ fechaInicio: null, fechaFin: null });
        clearSelectedOrganization();
    };

    const handleChangeFilter: HandleChangeExtraFilters = (e) => {
        if (!e?.target) return;
        const { name, value }: any = e?.target;
        if (name && !value) {
            setFilters((prevFilters: any) => {
                const newFilters = { ...(prevFilters || {}) };
                delete newFilters[name];
                return newFilters;
            });
        } else
            setFilters((prevFilters: any) => ({
                ...(prevFilters || {}),
                ...defaultFilters,
                [name]: value
            }));
    };

    const handleChangeExtraFilters: HandleChangeExtraFilters = (e) => {
        const { name, value, type, checked } = e.target as {
            name: string;
            value: any;
            type?: string;
            checked?: boolean;
        };

        //PROVISORY SOLUTION TO PREVENT ERRORS ON TRUE AND FALSE FILTERS (example: SI, NO, etc.)
        if (
            name.includes('presentacionRGRL') ||
            name.includes('presentacionRAR') ||
            name.includes('presentacionSVCC') ||
            name.includes('tieneCancerigenos') ||
            name.includes('tieneExpuestos')
        ) {
            setExtraFilters((prevExtraFilters: any) => ({
                ...(prevExtraFilters || {}),
                [name]: value
            }));
            handleSubmitSearch();
            return;
        }

        if (value === null || value === undefined || value === '') {
            setFilters((prevFilters: any) => {
                const newFilterObject = { ...(prevFilters || {}) };
                if (name === 'userState') {
                    if (newFilterObject.hasOwnProperty('activo')) delete newFilterObject.activo;
                    if (newFilterObject.hasOwnProperty('bloqueado')) delete newFilterObject.bloqueado;
                } else {
                    delete newFilterObject[name];
                }
                return newFilterObject;
            });

            setExtraFilters((prevExtraFilters: any) => {
                const newObject = { ...(prevExtraFilters || {}) };
                delete newObject[name];
                return newObject;
            });
        } else {
            if (type && type === 'checkbox') {
                setExtraFilters((prevExtraFilters: any) => ({
                    ...(prevExtraFilters || {}),
                    [name]: checked
                }));
            } else if (typeof value === 'object' && type && type !== 'autocomplete') {
                setExtraFilters((prevExtraFilters: any) => ({
                    ...(prevExtraFilters || {}),
                    [name]: value?.value || value?.nombre
                }));
            } else {
                setExtraFilters((prevExtraFilters: any) => ({
                    ...(prevExtraFilters || {}),
                    [name]: value
                }));
            }
        }
        setShouldSubmit(true);
    };

    const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
        setDateFilters({
            fechaInicio: startDate ? startDate.toISOString() : null,
            fechaFin: endDate ? endDate.toISOString() : null
        });
    };

    const handleSubmitSearch = () => {
        let newFilters: { [key: string]: any } = {};

        let controlledFilters = { ...filters };
        _.entries(extraFilters).forEach((key: any, value: any) => {
            if (Array.isArray(key[1])) {
                // Soporta filtros multi-select (arrays de ids u objetos {id}), ej. Contrato en Nómina (SMP-165)
                const arrayValue = key[1]
                    .map((item: any) => (item && typeof item === 'object' ? item?.id ?? item?.value ?? null : item))
                    .filter((item: any) => item !== null && item !== undefined && item !== '');

                if (arrayValue.length) {
                    newFilters[key[0]] = arrayValue;
                } else {
                    delete controlledFilters[key[0]];
                }
            } else if (typeof key[1] === 'object') {
                let value = key[1]?.nombre;
                if (['activo', 'ingreso'].includes(key[0])) value = key[1].value;
                if (key[0].includes('id')) value = key[1].id;
                if (key[0].includes('perfiles')) value = key[1].id;
                if (key[0] === 'examen' || key[0] === 'prestador') {
                    value = key[1].value;
                }
                if (key[0].includes('risks')) value = key[1].value;
                if (key[0].includes('expuesto')) value = key[1].value;
                if (key[0].includes('expositionDate')) value = key[1].value;
                if (key[0].includes('fecha')) value = key[1];
                if (key[0].includes('estado')) value = key[1].value;
                if (
                    key[0].includes('presentacionRGRL') ||
                    key[0].includes('presentacionRAR') ||
                    key[0].includes('presentacionSVCC')
                )
                    value = key[1].value;
                if (key[0].includes('idCiiu')) value = key[1].id;
                if (key[0].includes('organismo')) value = key[1].id;
                if (key[0].includes('nroContrato')) value = key[1].id;
                if (key[0].includes('idOrganismoCentral')) value = key[1].id;
                if (key[0].includes('idOrganismo')) value = key[1].id;
                if (key[0].includes('organismo')) value = key[1].id;
                if (key[0].includes('codigoRiesgo')) value = key[1].codigo;
                if (key[0].includes('idRol')) value = key[1].id;
                if (key[0].includes('userState')) {
                    let name = '';
                    if (['Activo', 'Inactivo'].includes(key[1].nombre)) {
                        name = 'activo';
                        if (controlledFilters.hasOwnProperty('bloqueado'))
                            delete controlledFilters?.bloqueado;
                    } else {
                        name = 'bloqueado';
                        if (controlledFilters.hasOwnProperty('activo'))
                            delete controlledFilters?.activo;
                    }
                    newFilters[name] = key[1].value;
                } else if (value !== '' && value !== null && value !== undefined) {
                    newFilters[key[0]] = value;
                } else {
                    delete controlledFilters[key[0]];
                }
            } else if (key[1] !== '' && key[1] !== null && key[1] !== undefined) {
                newFilters[key[0]] = key[1];
            } else {
                delete controlledFilters[key[0]];
            }
        });

        const cleanedDateFilters = Object.fromEntries(
            Object.entries(dateFilters).filter(([_, value]) => value !== null)
        );

        setFilters({
            ...controlledFilters,
            ...defaultFilters,
            ...newFilters,
            ...cleanedDateFilters
        });
        setShouldSubmit(false);
    };

    // Una sola instancia de debounce por montaje: si se recrea en cada render, un re-render
    // a mitad del tipeo deja timers viejos vivos y dispara un request por cada uno
    const handleChangeFilterRef = useRef(handleChangeFilter);
    handleChangeFilterRef.current = handleChangeFilter;

    const debounceSearch = useMemo(
        () => _.debounce((e: ChangeEventUnion) => handleChangeFilterRef.current(e), 700),
        []
    );

    useEffect(() => () => debounceSearch.cancel(), [debounceSearch]);

    useEffect(() => {
        if (!selectedOrganizationId) return;

        handleChangeExtraFilters({
            target: {
                name: 'idOrganizacion',
                value: selectedOrganizationId
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganizationId]);

    //This function is for cases where you need to filter data without filling input fields, for exapmle, when clicking a Card.
    const handleDirectFilter = (newFilters: any) => {
        setFilters({ ...defaultFilters, ...newFilters });
        setShouldSubmit(true);
    };

    return {
        filters,
        extraFilters,
        dateFilters,
        debounceSearch,
        setFilters,
        clearFilters,
        setExtraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters,
        handleDateChange,
        handleDirectFilter,
        shouldSubmit
    };
}

export default useGlobalFilters;
